import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import { Icon, Wordmark } from "@despejo/ui";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "./AuthProvider.js";

function Frame({ children, title, subtitle }: { children: ReactNode; title: string; subtitle?: string }) {
  return (
    <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", px: 2, bgcolor: "background.default" }}>
      <Card sx={{ width: "100%", maxWidth: 420 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Wordmark size={22} />
            <Box>
              <Typography variant="h4" component="h1">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {children}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Link from the e-mail: /auth/confirm?token_hash=...&type=email. Works on any device (no PKCE verifier needed).
  useEffect(() => {
    const url = new URL(window.location.href);
    const tokenHash = url.searchParams.get("token_hash");
    if (url.pathname !== "/auth/confirm" || !tokenHash) return;
    void (async () => {
      setBusy(true);
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "email" });
      window.history.replaceState(null, "", "/");
      setBusy(false);
      if (error) setError("El enlace ya se usó o venció. Pida uno nuevo.");
    })();
  }, []);

  const verifyCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.verifyOtp({ email, token: code.trim(), type: "email" });
    setBusy(false);
    if (error) setError("El código no es válido o ya venció. Revise el último correo o pida uno nuevo.");
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
    setBusy(false);
    if (error) setError(error.message);
    else setSent(true);
  };

  return (
    <Frame title="Ingresar" subtitle="Le enviamos un código de acceso a su correo. Sin contraseñas.">
      {sent ? (
        <Stack component="form" spacing={2} onSubmit={verifyCode}>
          <Alert icon={<Icon name="mark_email_read" />} severity="success">
            Le enviamos un código a {email}. Escríbalo aquí, o abra el enlace del correo en cualquier dispositivo.
          </Alert>
          <TextField label="Código del correo" autoComplete="one-time-code" required slotProps={{ htmlInput: { inputMode: "numeric" } }} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))} />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" size="large" disabled={busy || code.trim().length < 6}>
            Entrar
          </Button>
          <Button onClick={() => { setSent(false); setCode(""); setError(null); }}>Usar otro correo o pedir otro código</Button>
        </Stack>
      ) : (
        <Stack component="form" spacing={2} onSubmit={submit}>
          <TextField label="Correo electrónico" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" size="large" disabled={busy}>
            Enviar código
          </Button>
        </Stack>
      )}
    </Frame>
  );
}

export function EnrollMfaPage() {
  const { refresh } = useAuth();
  const [factor, setFactor] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      // Clean up half-finished enrollments from earlier attempts.
      const { data: list } = await supabase.auth.mfa.listFactors();
      for (const f of list?.all ?? []) if (f.status === "unverified") await supabase.auth.mfa.unenroll({ factorId: f.id });
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: `Despejo ${new Date().toISOString().slice(0, 10)}` });
      if (error) setError(error.message);
      else setFactor({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
    })();
  }, []);

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!factor) return;
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code: code.trim() });
    if (error) setError("El código no es válido. Intente con el código actual de su app.");
    else await refresh();
  };

  return (
    <Frame title="Proteja su cuenta" subtitle="Despejo guarda información fiscal. Configure un segundo factor con una app de autenticación (Google Authenticator, 1Password, Authy).">
      {factor && (
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Box component="img" src={factor.qr} alt="Código QR para la app de autenticación" sx={{ width: 200, height: 200, bgcolor: "#fff", p: 1, borderRadius: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", textAlign: "center" }}>
            ¿No puede escanear? Código: {factor.secret}
          </Typography>
        </Stack>
      )}
      <Stack component="form" spacing={2} onSubmit={verify}>
        <TextField label="Código de 6 dígitos" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} slotProps={{ htmlInput: { pattern: "[0-9]{6}", maxLength: 6 } }} />
        {error && <Alert severity="error">{error}</Alert>}
        <Button type="submit" variant="contained" size="large" disabled={!factor}>
          Activar
        </Button>
        <Button onClick={() => supabase.auth.signOut()}>Salir</Button>
      </Stack>
    </Frame>
  );
}

export function VerifyMfaPage({ factorId }: { factorId: string }) {
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
    if (error) setError("El código no es válido.");
    else await refresh();
  };
  return (
    <Frame title="Verificación" subtitle="Ingrese el código de su app de autenticación.">
      <Stack component="form" spacing={2} onSubmit={verify}>
        <TextField label="Código de 6 dígitos" inputMode="numeric" autoComplete="one-time-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} slotProps={{ htmlInput: { pattern: "[0-9]{6}", maxLength: 6 } }} />
        {error && <Alert severity="error">{error}</Alert>}
        <Button type="submit" variant="contained" size="large">
          Verificar
        </Button>
        <Button onClick={() => supabase.auth.signOut()}>Salir</Button>
      </Stack>
    </Frame>
  );
}
